import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { AccountInfo } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import {
  msalInstance,
  getActiveAccount,
  signIn,
  signOut,
} from "../services/authService";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AccountInfo | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const account = getActiveAccount();
    setUser(account);
    setIsLoading(false);

    // Register event callbacks for account changes
    const accountChangedCallback = (message: any) => {
      if (message?.payload) {
        const account = getActiveAccount();
        setUser(account);
      }
    };

    // Register callback for account changes
    msalInstance.addEventCallback(accountChangedCallback);

    return () => {
      msalInstance.removeEventCallback(accountChangedCallback);
    };
  }, []);

  const login = async (): Promise<void> => {
    await signIn();
  };

  const logout = async (): Promise<void> => {
    await signOut();
    setUser(null);
  };

  const contextValue = useMemo(
    () => ({
      isAuthenticated: user !== null,
      isLoading,
      user,
      login,
      logout,
    }),
    [isLoading, user],
  );

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContext.Provider value={contextValue}>
        {children}
      </AuthContext.Provider>
    </MsalProvider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
